import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  TextField,
  InputAdornment,
  Zoom,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

// Swiper 6 Legacy
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation, Keyboard } from 'swiper';
import 'swiper/swiper.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';

import styles from './SecurityDashboard.module.css';
import { userInfoSelector } from '../../redux/selectors/auth';
import { BASE_URL } from '../global/Server/endpoints';
import axios from 'axios';
import AuthUtil from '../../redux/AuthUtil';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { HourglassEmpty } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DashboardIcon from '@mui/icons-material/Dashboard';

import { Search as SearchIcon, Sort as SortIcon } from '@mui/icons-material';
import withShipment from '../../withShipment';
import { DialogContent } from '@material-ui/core';
import { blacklistAlertSelector } from '../../redux/selectors/ticketOut';
import { setBlacklistAlert } from '../../redux/actions/settings';

SwiperCore.use([Pagination, Navigation, Keyboard]);
SwiperCore.defaults.touchStartPreventDefault = false;
SwiperCore.defaults.touchMoveStopPropagation = true;

const IMAGE_BASE = process.env.REACT_APP_API_URL || '';

class SecurityDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      entries: [],
      connected: false,
      loading: false,
      filterValue: 'PENDING',
      previewImage: null,
      updatingId: null,
      showLogoutConfirm: false,
      searchQuery: '',
      sortNewest: true,
      selectedCustomer: null,
      swipingId: null,
      swipeX: 0,
      isSwipeAction: false,
      MAX_SWIPE: -120,
      ACTION_THRESHOLD: -80,

      //alert
      isImageZoomed: false,

      //blacklist screenshot
      activeView: 'dashboard', // 'dashboard' | 'slideshow'
      blacklistCustomers: [], // fetched blacklist photo data
      slideshowIndex: 0,
      slideshowPaused: false,
      slideshowInterval: null,
    };
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isDragging = false;
    this.socket = null;
    this.wakeLock = null;
  }

  componentDidMount() {
    this.fetchHistory();
    this.connectWebSocket();
    this.requestWakeLock();
    document.addEventListener(
      'visibilitychange',
      this.handleWakeLockVisibility
    );
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.user?.selectedLocationId !== this.props.user?.selectedLocationId
    ) {
      this.fetchHistory();
      this.connectWebSocket();
    }
  }

  componentWillUnmount() {
    this.closeSocket();
    // Clean up Wake Lock
    if (this.wakeLock) {
      this.wakeLock.release();
      this.wakeLock = null;
    }
    document.removeEventListener(
      'visibilitychange',
      this.handleWakeLockVisibility
    );
  }

  startSlideshow = () => {
    const interval = setInterval(() => {
      this.setState((prev) => ({
        slideshowIndex:
          (prev.slideshowIndex + 1) % prev.blacklistCustomers.length,
      }));
    }, 6500);
    this.setState({ slideshowInterval: interval, slideshowPaused: false });
  };

  pauseSlideshow = () => {
    clearInterval(this.state.slideshowInterval);
    this.setState({ slideshowPaused: true, slideshowInterval: null });
  };

  exitSlideshow = () => {
    clearInterval(this.state.slideshowInterval);
    this.setState({
      activeView: 'dashboard',
      slideshowIndex: 0,
      slideshowPaused: false,
    });
  };

  fetchBlacklistCustomers = async () => {
    const { user } = this.props;
    const locationId = user?.selectedLocationId;
    if (!locationId) return;

    try {
      const response = await axios.get(
        `${BASE_URL}/security/getBlacklistCustomers?location=${locationId}`,
        { headers: { Authorization: `Bearer ${AuthUtil.getToken()}` } }
      );
      this.setState({ blacklistCustomers: response.data || [] });
    } catch (err) {
      console.error('Failed to fetch blacklist customers:', err);
    }
  };

  requestWakeLock = async () => {
    // Guard clause: API support + Desktop only + Visibility
    if (
      !('wakeLock' in navigator) ||
      this.state.isMobile ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }

    try {
      if (!this.wakeLock) {
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null;
        });
      }
    } catch (err) {
      console.error('Wake Lock failed:', err.message);
    }
  };

  handleWakeLockVisibility = () => {
    if (document.visibilityState === 'visible' && !this.state.isMobile) {
      this.requestWakeLock();
    }
  };

  closeSocket = () => {
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  };

  fetchHistory = async () => {
    const { user } = this.props;
    const locationId = user?.selectedLocationId;
    if (!locationId) return;

    this.setState({ loading: true });
    try {
      const response = await axios.get(
        `${BASE_URL}/security/getGateEntries?location=${locationId}`,
        { headers: { Authorization: `Bearer ${AuthUtil.getToken()}` } }
      );
      this.setState({ entries: response.data || [] });
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      this.setState({ loading: false });
    }
  };

  // FIX: Use gateEntryId to match the updated Backend updateStatus method
  updateStatus = async (gateEntryId, newStatus) => {
    this.setState({ updatingId: gateEntryId });
    try {
      await axios.post(
        `${BASE_URL}/security/updateStatus`,
        { gateEntryId, status: newStatus },
        { headers: { Authorization: `Bearer ${AuthUtil.getToken()}` } }
      );

      this.setState((prevState) => {
        // 1. If Checked Out, remove from list entirely. Otherwise, update status.
        const updatedEntries =
          newStatus === 'CHECKED_OUT'
            ? prevState.entries.filter((e) => e.gateEntryId !== gateEntryId)
            : prevState.entries.map((e) =>
                e.gateEntryId === gateEntryId ? { ...e, status: newStatus } : e
              );

        // 2. If the person we just updated is the one showing on the card,
        // and they are now Checked Out or Denied (moving tabs), close the card.
        const shouldClearCard =
          prevState.selectedCustomer?.gateEntryId === gateEntryId;

        return {
          entries: updatedEntries,
          selectedCustomer: shouldClearCard ? null : prevState.selectedCustomer,
          updatingId: null,
        };
      });
    } catch (err) {
      console.error('Status update failed', err);
      this.setState({ updatingId: null });
    }
  };

  connectWebSocket = () => {
    this.closeSocket();
    const { user } = this.props;
    const locationId = user?.selectedLocationId;
    if (!locationId) return;

    const wsUrl = `${process.env.REACT_APP_WS_URL}?location=${locationId}&type=security`;
    this.socket = new WebSocket(wsUrl);

    // this.socket.onopen = () => this.setState({ connected: true });

    this.socket.onopen = () => {
      this.setState({ connected: true });
      // Start keep-alive pings
      this.socketKeepAlive = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // every 30 sec
    };

    this.socket.onclose = () => {
      this.setState({ connected: false });
      if (this.socket) setTimeout(this.connectWebSocket, 3000);
    };

    this.socket.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.action === 'NEW_GATE_ENTRY') {
          this.setState((prev) => ({ entries: [msg.data, ...prev.entries] }));
        } else if (msg.action === 'black-list-alert') {
          // 1. Set the alert data into state to be passed to children
          this.props.setBlacklistAlert({
            show: true,
            customer: msg.customer,
          });
        } else if (msg.action === 'refreshData') {
          this.fetchHistory();
        }
      } catch (err) {
        console.error('WS Error:', err);
      }
    };
  };

  getFilteredData = () => {
    const { entries, filterValue, searchQuery, sortNewest } = this.state;

    return [...entries]
      .filter((e) => e.status === filterValue)
      .filter((e) =>
        e.customerName.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        // Standardize the p.m./a.m. dots to PM/AM for the Date constructor
        const timeAStr = a.timestamp.replace(/\./g, '').toUpperCase();
        const timeBStr = b.timestamp.replace(/\./g, '').toUpperCase();

        // Create a comparable date by adding a dummy date prefix
        const timeA = new Date(`1970/01/01 ${timeAStr}`).getTime();
        const timeB = new Date(`1970/01/01 ${timeBStr}`).getTime();

        const valA = isNaN(timeA) ? 0 : timeA;
        const valB = isNaN(timeB) ? 0 : timeB;

        return sortNewest ? valB - valA : valA - valB;
      });
  };

  renderAllowedTab = () => {
    const { selectedCustomer, searchQuery, sortNewest } = this.state;
    const data = this.getFilteredData();

    return (
      <Box sx={{ display: 'flex', height: '80vh', gap: 2, mt: 2 }}>
        {/* LEFT SIDEBAR: Search and List */}
        <Box
          sx={{
            width: '350px',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <TextField
              fullWidth
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                input: { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#aaa', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => this.setState({ sortNewest: !sortNewest })}
                      sx={{ color: '#aaa' }}
                    >
                      <SortIcon
                        sx={{
                          transform: sortNewest ? 'none' : 'rotate(180deg)',
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Visitor List */}
          <Box
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {data.map((row) => {
              return (
                <Box
                  key={row.gateEntryId}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* 🔴 BACKGROUND ACTION */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#d32f2f',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 3,

                      // 👇 KEY PART
                      opacity:
                        this.state.swipingId === row.gateEntryId &&
                        Math.abs(this.state.swipeX) > 70
                          ? 1
                          : 0,

                      transition: 'opacity 0.2s ease',
                      pointerEvents: 'none', // prevent accidental taps
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        letterSpacing: 0.5,
                      }}
                    >
                      {Math.abs(this.state.swipeX) > 80
                        ? 'RELEASE TO CLOCK OUT'
                        : 'CLOCK OUT'}
                    </Typography>
                  </Box>

                  {/* 🟢 FOREGROUND (SLIDING ROW) */}
                  <Box
                    onPointerDown={(e) => {
                      this.pointerStartX = e.clientX;
                      this.pointerStartY = e.clientY;
                      this.isDragging = true;

                      this.setState({
                        swipingId: row.gateEntryId,
                        swipeX: 0,
                        isSwipeAction: false,
                      });

                      e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                      if (!this.isDragging) return;

                      const dx = e.clientX - this.pointerStartX;
                      const dy = e.clientY - this.pointerStartY;

                      if (Math.abs(dy) > Math.abs(dx)) {
                        this.isDragging = false;
                        return;
                      }

                      if (dx < 0) {
                        this.setState({
                          swipeX: Math.max(dx, -120),
                          isSwipeAction: Math.abs(dx) > 15,
                        });
                      }
                    }}
                    onPointerUp={() => {
                      this.isDragging = false;

                      const { swipeX } = this.state;

                      if (swipeX <= -80) {
                        // ✅ Full swipe → trigger action
                        this.updateStatus(row.gateEntryId, 'CHECKED_OUT');

                        // Optional: keep it closed after action
                        this.setState({
                          swipingId: null,
                          swipeX: 0,
                          isSwipeAction: false,
                        });
                      } else {
                        // ❌ Small swipe → SNAP BACK
                        this.setState({
                          swipingId: null,
                          swipeX: 0,
                          isSwipeAction: false,
                        });
                      }
                    }}
                    onPointerCancel={() => {
                      this.isDragging = false;
                      this.setState({
                        swipingId: null,
                        swipeX: 0,
                        isSwipeAction: false,
                      });
                    }}
                    onClick={() => {
                      if (this.state.isSwipeAction) return;
                      this.setState({ selectedCustomer: row });
                    }}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 1,

                      willChange: 'transform',
                      transform:
                        this.state.swipingId === row.gateEntryId
                          ? `translateX(${this.state.swipeX}px)`
                          : 'translateX(0)',
                      transition:
                        this.state.swipingId === row.gateEntryId
                          ? 'none'
                          : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',

                      bgcolor:
                        selectedCustomer?.gateEntryId === row.gateEntryId
                          ? 'rgba(255,255,255,0.1)'
                          : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    {/* LEFT CONTENT */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Avatar
                        src={`${IMAGE_BASE}${row.picture}`}
                        sx={{ width: 40, height: 40, borderRadius: '50%' }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                          }}
                        >
                          {row.customerName}
                        </Typography>
                      </Box>
                    </Box>

                    {/* RIGHT CONTENT */}
                    {!(Math.abs(this.state.swipeX) > 80) && (
                      <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>
                        {row.timestamp}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT SIDE: Profile Card */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: 6, // Slightly more top padding for breathing room
          }}
        >
          {selectedCustomer ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: '850px', // Wider to match reference aspect ratio
                bgcolor: 'white',
                borderRadius: '24px', // Smoother corners
                display: 'flex',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                overflow: 'visible', // Allows icon badge to pop
              }}
            >
              {/* Left: Picture Section */}
              <Box sx={{ p: 4, position: 'relative' }}>
                <Avatar
                  onClick={() => {
                    this.setState({
                      previewImage: selectedCustomer.picture,
                    });
                  }}
                  variant="rounded"
                  src={`${IMAGE_BASE}${selectedCustomer.picture}`}
                  sx={{
                    cursor: 'zoom-in',
                    width: 320,
                    height: 320,
                    borderRadius: '20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                />
                {/* Verifed Check - Positioned to overlap image slightly */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 50,
                    right: 20,
                    bgcolor: 'green', // Change from Orange to Navy (Matches your Check-In theme)
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '4px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  <CheckIcon sx={{ color: 'white', fontSize: 26 }} />
                </Box>
              </Box>

              {/* Right: Info Section */}
              <Box
                sx={{
                  flex: 1,
                  pl: 2,
                  pr: 5,
                  py: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: '#2c3e50', // Deep dark grey/blue text
                    fontSize: '3.5rem', // Large bold name
                    letterSpacing: '-0.02em',
                    mb: 0.5,
                  }}
                >
                  {selectedCustomer.customerName.toUpperCase()}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: '#95a5a6', // Muted grey for timestamp
                    fontWeight: 500,
                    mb: 6,
                  }}
                >
                  {selectedCustomer.timestamp}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0 }}>
                  {/* Check In Button - Dark Navy */}
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 28 }} />}
                    sx={{
                      bgcolor: '#1e2b58',
                      height: '64px',
                      fontWeight: 800,
                      fontSize: '1.2rem',
                      textTransform: 'none',
                      borderTopLeftRadius: '14px',
                      borderBottomLeftRadius: '14px',
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      '&:hover': { bgcolor: '#151d3d' },
                    }}
                    onClick={() =>
                      this.updateStatus(
                        selectedCustomer.gateEntryId,
                        'CHECKED_OUT'
                      )
                    }
                  >
                    CHECK OUT
                  </Button>

                  {/* Check Out Button - Orange */}
                  {/*<Button*/}
                  {/*  variant="contained"*/}
                  {/*  fullWidth*/}
                  {/*  startIcon={<CloseIcon sx={{ fontSize: 28 }} />}*/}
                  {/*  sx={{*/}
                  {/*    bgcolor: '#f28c28',*/}
                  {/*    height: '64px',*/}
                  {/*    fontWeight: 800,*/}
                  {/*    fontSize: '1.2rem',*/}
                  {/*    textTransform: 'none',*/}
                  {/*    borderTopLeftRadius: 0,*/}
                  {/*    borderBottomLeftRadius: 0,*/}
                  {/*    borderTopRightRadius: '14px',*/}
                  {/*    borderBottomRightRadius: '14px',*/}
                  {/*    '&:hover': { bgcolor: '#d3751a' },*/}
                  {/*  }}*/}
                  {/*  onClick={() =>*/}
                  {/*    this.updateStatus(selectedCustomer.gateEntryId, 'DENIED')*/}
                  {/*  }*/}
                  {/*>*/}
                  {/*  DENY ENTRY*/}
                  {/*</Button>*/}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', mt: 10 }}>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem' }}
              >
                Select a visitor from the list to view details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  renderCheckedOutTab = () => {
    const { selectedCustomer, searchQuery, sortNewest } = this.state;
    const data = this.getFilteredData();

    return (
      <Box sx={{ display: 'flex', height: '80vh', gap: 2, mt: 2 }}>
        {/* LEFT SIDEBAR: Search and List */}
        <Box
          sx={{
            width: '350px',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <TextField
              fullWidth
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                input: { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#aaa', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => this.setState({ sortNewest: !sortNewest })}
                      sx={{ color: '#aaa' }}
                    >
                      <SortIcon
                        sx={{
                          transform: sortNewest ? 'none' : 'rotate(180deg)',
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Visitor List */}
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {data.map((row) => (
              <Box
                key={row.gateEntryId}
                onClick={() => this.setState({ selectedCustomer: row })}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: '0.2s',
                  bgcolor:
                    selectedCustomer?.gateEntryId === row.gateEntryId
                      ? 'rgba(255,255,255,0.1)'
                      : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    src={`${IMAGE_BASE}${row.picture}`}
                    sx={{ width: 40, height: 40, borderRadius: '50%' }} // Matches small circular icons in list
                  />
                  <Typography
                    sx={{ color: 'white', fontSize: '0.9rem', fontWeight: 500 }}
                  >
                    {row.customerName}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>
                  {row.timestamp}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* RIGHT SIDE: Profile Card */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: 6, // Slightly more top padding for breathing room
          }}
        >
          {selectedCustomer ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: '850px', // Wider to match reference aspect ratio
                bgcolor: 'white',
                borderRadius: '24px', // Smoother corners
                display: 'flex',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                overflow: 'visible', // Allows icon badge to pop
              }}
            >
              {/* Left: Picture Section */}
              <Box sx={{ p: 4, position: 'relative' }}>
                <Avatar
                  onClick={() => {
                    this.setState({
                      previewImage: selectedCustomer.picture,
                    });
                  }}
                  variant="rounded"
                  src={`${IMAGE_BASE}${selectedCustomer.picture}`}
                  sx={{
                    cursor: 'zoom-in',
                    width: 320,
                    height: 320,
                    borderRadius: '20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                />
                {/* Verifed Check - Positioned to overlap image slightly */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 50,
                    right: 20,
                    bgcolor: 'green', // Change from Orange to Navy (Matches your Check-In theme)
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '4px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  <CheckIcon sx={{ color: 'white', fontSize: 26 }} />
                </Box>
              </Box>

              {/* Right: Info Section */}
              <Box
                sx={{
                  flex: 1,
                  pl: 2,
                  pr: 5,
                  py: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      color: '#2c3e50', // Deep dark grey/blue text
                      fontSize: '3.5rem', // Large bold name
                      letterSpacing: '-0.02em',
                      mb: 0.5,
                    }}
                  >
                    {selectedCustomer.customerName.toUpperCase()}
                  </Typography>
                  <div className={styles.rowSpecTime}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'green',
                        fontWeight: 700,
                        display: 'block',
                      }}
                    >
                      In: {selectedCustomer.timestamp}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#d32f2f',
                        fontWeight: 700,
                        display: 'block',
                      }}
                    >
                      Out: {selectedCustomer.checkOutDate}
                    </Typography>
                  </div>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#1976d2',
                      fontWeight: 700,
                      display: 'block',
                    }}
                  >
                    Stayed: {selectedCustomer.duration}
                  </Typography>
                </>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', mt: 10 }}>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem' }}
              >
                Select a visitor from the list to view details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  renderSlideshowTab = () => {
    const { blacklistCustomers, slideshowIndex, slideshowPaused } = this.state;

    /* ── Empty state ── */
    if (blacklistCustomers.length === 0) {
      return (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 4,
            bgcolor: '#0a0a0a',
            zIndex: 1200,
          }}
        >
          <Typography sx={{ fontSize: '5rem' }}>
            <span role="img" aria-label="No entry">
              ⛔
            </span>
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '2rem',
              fontWeight: 700,
            }}
          >
            No blacklisted customers on record
          </Typography>
          <Button
            variant="outlined"
            onClick={this.exitSlideshow}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              fontWeight: 700,
              fontSize: '1.1rem',
              px: 5,
              py: 1.5,
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            ← Back to Dashboard
          </Button>
        </Box>
      );
    }

    const current = blacklistCustomers[slideshowIndex];

    return (
      /*
       * Fixed overlay — covers the ENTIRE screen including the left nav.
       * This gives a true TV full-screen experience.
       */
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: '#080808',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {/* ══════════ TOP PROGRESS BAR ══════════ */}
        <Box
          sx={{
            position: 'relative',
            height: '6px',
            bgcolor: 'rgba(255,255,255,0.06)',
            flexShrink: 0,
          }}
        >
          <Box
            key={`${slideshowIndex}-${slideshowPaused}`}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              bgcolor: '#d32f2f',
              animation: slideshowPaused
                ? 'none'
                : 'tvProgress 6.5s linear forwards',
              '@keyframes tvProgress': {
                from: { width: '0%' },
                to: { width: '100%' },
              },
              boxShadow: '0 0 12px #d32f2f',
            }}
          />
        </Box>

        {/* ══════════ TOP HUD BAR ══════════ */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 5,
            py: 2,
            flexShrink: 0,
          }}
        >
          {/* Left: branding */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                bgcolor: '#b71c1c',
                px: 2.5,
                py: 0.8,
                borderRadius: '8px',
                fontWeight: 900,
                fontSize: '1rem',
                color: 'white',
                letterSpacing: 3,
                boxShadow: '0 0 20px rgba(183,28,28,0.5)',
              }}
            >
              <span role="img" aria-label="no entry sign">
                ⛔
              </span>{' '}
              DO NOT ALLOW ENTRY
            </Box>
          </Box>

          {/* Center: PAUSED badge */}
          {slideshowPaused && (
            <Box
              sx={{
                bgcolor: 'rgba(255,160,0,0.15)',
                border: '2px solid rgba(255,160,0,0.5)',
                color: '#ffa000',
                px: 3,
                py: 0.8,
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 900,
                letterSpacing: 3,
              }}
            >
              ⏸ PAUSED
            </Box>
          )}

          {/* Right: counter */}
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '1.1rem',
              fontWeight: 800,
              letterSpacing: 3,
            }}
          >
            {slideshowIndex + 1} / {blacklistCustomers.length}
          </Typography>
        </Box>

        {/* ══════════ MAIN CONTENT — horizontal split ══════════ */}
        <Box
          key={slideshowIndex}
          sx={{
            flex: 1,
            display: 'flex',
            gap: 0,
            overflow: 'hidden',
            animation: 'tvFadeIn 0.5s ease',
            '@keyframes tvFadeIn': {
              from: { opacity: 0, transform: 'scale(0.98)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          {/* ── LEFT: Giant photo ── */}
          <Box
            sx={{
              width: '55%',
              flexShrink: 0,
              position: 'relative',
              p: 4,
              pr: 2,
            }}
          >
            {/* Red glow behind photo */}
            <Box
              sx={{
                position: 'absolute',
                inset: '10%',
                bgcolor: 'rgba(211,47,47,0.2)',
                filter: 'blur(60px)',
                borderRadius: '50%',
                zIndex: 0,
              }}
            />
            <Box
              component="img"
              src={current.picture}
              alt={`${current.firstName} ${current.lastName}`}
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
                borderRadius: '24px',
                boxShadow:
                  '0 0 0 5px #d32f2f, 0 30px 100px rgba(211,47,47,0.5)',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>

          {/* ── RIGHT: Info panel ── */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: 7,
              gap: 4,
            }}
          >
            {/* BLACKLISTED pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                bgcolor: '#b71c1c',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: '12px',
                fontWeight: 900,
                fontSize: '1.1rem',
                letterSpacing: 4,
                boxShadow: '0 4px 24px rgba(183,28,28,0.6)',
              }}
            >
              BLACKLISTED
            </Box>

            {/* Full name — massive for TV readability */}
            <Typography
              sx={{
                color: 'white',
                fontSize: 'clamp(3.5rem, 6vw, 7rem)',
                fontWeight: 900,
                lineHeight: 1,
                textTransform: 'uppercase',
                letterSpacing: '-0.02em',
                textShadow: '0 4px 30px rgba(0,0,0,0.8)',
              }}
            >
              {current.firstName}
              <br />
              {current.lastName}
            </Typography>

            {/* Divider */}
            <Box
              sx={{
                width: '80px',
                height: '5px',
                bgcolor: '#d32f2f',
                borderRadius: 2,
              }}
            />

            {/* Reason box */}
            {current.blockReason && (
              <Box
                sx={{
                  bgcolor: 'rgba(211,47,47,0.1)',
                  border: '2px solid rgba(211,47,47,0.3)',
                  borderLeft: '6px solid #d32f2f',
                  borderRadius: '12px',
                  px: 4,
                  py: 3,
                  maxWidth: '560px',
                }}
              >
                <Typography
                  sx={{
                    color: '#ef9a9a',
                    fontSize: '0.85rem',
                    fontWeight: 900,
                    letterSpacing: 4,
                    mb: 1,
                  }}
                >
                  REASON FOR RESTRICTION
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1.6rem',
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                    fontWeight: 500,
                  }}
                >
                  "{current.blockReason}"
                </Typography>
              </Box>
            )}

            {/* Dot indicators */}
            {blacklistCustomers.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                {blacklistCustomers.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => this.setState({ slideshowIndex: i })}
                    sx={{
                      width: i === slideshowIndex ? 32 : 10,
                      height: 10,
                      borderRadius: '5px',
                      bgcolor:
                        i === slideshowIndex
                          ? '#d32f2f'
                          : 'rgba(255,255,255,0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor:
                          i === slideshowIndex
                            ? '#d32f2f'
                            : 'rgba(255,255,255,0.4)',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* ══════════ BOTTOM CONTROLS ══════════ */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            py: 3,
            flexShrink: 0,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Button
            variant="outlined"
            onClick={this.exitSlideshow}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              borderColor: 'rgba(255,255,255,0.2)',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.2,
              '&:hover': {
                borderColor: 'white',
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            ← Exit Slideshow
          </Button>

          <Button
            variant="contained"
            onClick={
              slideshowPaused ? this.startSlideshow : this.pauseSlideshow
            }
            sx={{
              bgcolor: slideshowPaused ? '#1e2b58' : 'rgba(255,255,255,0.12)',
              fontWeight: 800,
              minWidth: 180,
              fontSize: '1rem',
              py: 1.2,
              '&:hover': {
                bgcolor: slideshowPaused ? '#151d3d' : 'rgba(255,255,255,0.18)',
              },
            }}
          >
            {slideshowPaused ? '▶  Resume' : '⏸  Pause'}
          </Button>

          <Button
            variant="outlined"
            onClick={() =>
              this.setState((prev) => ({
                slideshowIndex:
                  (prev.slideshowIndex + 1) % prev.blacklistCustomers.length,
              }))
            }
            sx={{
              color: 'rgba(255,255,255,0.6)',
              borderColor: 'rgba(255,255,255,0.2)',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.2,
              '&:hover': {
                borderColor: 'white',
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            Next →
          </Button>
        </Box>
      </Box>
    );
  };

  render() {
    const {
      connected,
      loading,
      entries,
      filterValue,
      previewImage,
      updatingId,
      searchQuery,
      sortNewest,
      activeView,
    } = this.state;

    const counts = {
      PENDING:     entries.filter((e) => e.status === 'PENDING').length,
      ALLOWED:     entries.filter((e) => e.status === 'ALLOWED').length,
      DENIED:      entries.filter((e) => e.status === 'DENIED').length,
      CHECKED_OUT: entries.filter((e) => e.status === 'CHECKED_OUT').length,
    };

    const activeVisitors = entries.filter((e) => e.status === 'ALLOWED').length;

    const filtered = entries
      .filter((e) => e.status === filterValue)
      .filter((e) =>
        e.customerName.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) =>
        sortNewest
          ? new Date(b.timestamp) - new Date(a.timestamp)
          : new Date(a.timestamp) - new Date(b.timestamp)
      );

    const chunked = [];
    for (let i = 0; i < filtered.length; i += 8)
      chunked.push(filtered.slice(i, i + 8));

    return (
      <div className={styles.dashboardWrapper}>
        {/* ══════════════════════════════════════════════════════
           OUTER FLEX ROW: left nav  +  main content
      ══════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          {/* ── LEFT NAVIGATION SIDEBAR ── */}
          <Box
            sx={{
              width: '72px',
              flexShrink: 0,
              bgcolor: 'rgba(255,255,255,0.03)',
              borderRight: '1px solid rgba(255,255,255,0.07)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 3,
              gap: 1,
              marginRight: '16px',
            }}
          >
            {/* Dashboard button */}
            <Tooltip title="Dashboard" placement="right">
              <IconButton
                onClick={() => {
                  clearInterval(this.state.slideshowInterval);
                  this.setState({
                    activeView: 'dashboard',
                    slideshowIndex: 0,
                    slideshowPaused: false,
                    slideshowInterval: null,
                  });
                }}
                sx={{
                  color: activeView === 'dashboard' ? '#00e676' : '#555',
                  bgcolor:
                    activeView === 'dashboard'
                      ? 'rgba(0,230,118,0.1)'
                      : 'transparent',
                  borderRadius: '14px',
                  width: 48,
                  height: 48,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'white',
                  },
                }}
              >
                <DashboardIcon />
              </IconButton>
            </Tooltip>

            {/* Slideshow button */}
            <Tooltip title="Blacklist Slideshow" placement="right">
              <IconButton
                onClick={async () => {
                  // clear any existing interval first
                  clearInterval(this.state.slideshowInterval);
                  await this.fetchBlacklistCustomers();
                  this.setState(
                    {
                      activeView: 'slideshow',
                      slideshowIndex: 0,
                      slideshowPaused: false,
                      slideshowInterval: null,
                    },
                    () => this.startSlideshow()
                  );
                }}
                sx={{
                  color: activeView === 'slideshow' ? '#ef5350' : '#555',
                  bgcolor:
                    activeView === 'slideshow'
                      ? 'rgba(211,47,47,0.15)'
                      : 'transparent',
                  borderRadius: '14px',
                  width: 48,
                  height: 48,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(211,47,47,0.1)',
                    color: '#ef5350',
                  },
                }}
              >
                <SlideshowIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* ── MAIN CONTENT AREA ── */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {activeView === 'slideshow' ? (
              /* ═══════════════════ SLIDESHOW VIEW ═══════════════════ */
              <Box sx={{ flex: 1, p: 3 }}>{this.renderSlideshowTab()}</Box>
            ) : (
              /* ═══════════════════ DASHBOARD VIEW ═══════════════════ */
              <div>
                {/* ── Header ── */}
                <header className={styles.headerContainer}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 900,
                    }}
                  >
                    <SensorsIcon
                      sx={{
                        fontSize: 45,
                        color: connected ? '#00e676' : '#ff1744',
                        mr: 2,
                      }}
                    />
                    GATE ACCESS
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Tabs
                      value={filterValue}
                      onChange={(e, v) =>
                        this.setState({
                          filterValue: v,
                          selectedCustomer: null,
                        })
                      }
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        p: 0.5,
                        '& .MuiTab-root': {
                          color: '#aaa',
                          fontWeight: 700,
                          minWidth: 120,
                        },
                        '& .Mui-selected': { color: '#00e676 !important' },
                        '& .MuiTabs-indicator': {
                          bgcolor: '#00e676',
                          height: 3,
                        },
                      }}
                    >
                      <Tab
                        label={`PENDING (${counts.PENDING})`}
                        value="PENDING"
                      />
                      <Tab
                        label={`CHECKED IN (${counts.ALLOWED})`}
                        value="ALLOWED"
                      />
                      <Tab
                        label={`CHECKED OUT (${counts.CHECKED_OUT})`}
                        value="CHECKED_OUT"
                      />
                      <Tab
                        label={`BLACK LIST (${counts.DENIED})`}
                        value="DENIED"
                      />
                    </Tabs>

                    <Tooltip title="Refresh">
                      <IconButton
                        onClick={this.fetchHistory}
                        sx={{
                          color: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <RefreshIcon />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Chip
                      label={connected ? 'LIVE' : 'OFFLINE'}
                      color={connected ? 'success' : 'error'}
                      sx={{ fontWeight: 900 }}
                    />

                    <Tooltip title="Logout">
                      <IconButton
                        onClick={() =>
                          this.setState({ showLogoutConfirm: true })
                        }
                        sx={{
                          color: 'white',
                          bgcolor: 'rgba(255, 23, 68, 0.1)',
                          '&:hover': { bgcolor: '#ff1744' },
                        }}
                      >
                        <LogoutIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </header>

                {/* ── Active visitors count ── */}
                <div>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 900,
                        marginTop: '-16px',
                      }}
                    >
                      Active Visitors ({activeVisitors})
                    </Typography>
                  </Box>
                </div>

                {/* ── Tab content ── */}
                {filterValue === 'ALLOWED' ? (
                  this.renderAllowedTab()
                ) : filterValue === 'CHECKED_OUT' ? (
                  this.renderCheckedOutTab()
                ) : (
                  <Swiper
                    spaceBetween={30}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    className={styles.mainSwiper}
                  >
                    {chunked.length > 0 ? (
                      chunked.map((slide, idx) => (
                        <SwiperSlide key={idx}>
                          <Grid
                            container
                            spacing={4}
                            sx={{ p: 2, marginTop: '-10px' }}
                          >
                            {slide.map((customer) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={customer.gateEntryId}
                              >
                                <Box
                                  sx={{
                                    bgcolor: 'white',
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                      transform: 'translateY(-5px)',
                                    },
                                  }}
                                >
                                  {/* Image */}
                                  <Box sx={{ position: 'relative', p: 1.5 }}>
                                    <Avatar
                                      variant="rounded"
                                      src={`${IMAGE_BASE}${customer.picture}`}
                                      onClick={() =>
                                        this.setState({
                                          previewImage: customer.picture,
                                        })
                                      }
                                      sx={{
                                        width: '100%',
                                        height: '240px',
                                        borderRadius: '16px',
                                        cursor: 'zoom-in',
                                        boxShadow:
                                          'inset 0 0 10px rgba(0,0,0,0.1)',
                                      }}
                                    />

                                    {/* Status badge */}
                                    {customer.status === 'CHECKED_OUT' ? (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 25,
                                          right: 25,
                                          bgcolor: 'green',
                                          borderRadius: '50%',
                                          width: 36,
                                          height: 36,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '4px solid white',
                                          boxShadow:
                                            '0 4px 12px rgba(0,0,0,0.3)',
                                        }}
                                      >
                                        <CheckIcon
                                          sx={{ color: 'white', fontSize: 26 }}
                                        />
                                      </Box>
                                    ) : (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 25,
                                          right: 25,
                                          bgcolor:
                                            customer.status === 'DENIED'
                                              ? '#d50000'
                                              : '#ef6c00',
                                          borderRadius: '50%',
                                          width: 36,
                                          height: 36,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '3px solid white',
                                          boxShadow:
                                            '0 2px 8px rgba(0,0,0,0.2)',
                                        }}
                                      >
                                        {customer.status === 'DENIED' ? (
                                          <CancelIcon
                                            sx={{
                                              fontSize: 20,
                                              color: 'white',
                                            }}
                                          />
                                        ) : (
                                          <HourglassEmpty
                                            className={styles.spinning}
                                            sx={{
                                              fontSize: 20,
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Text */}
                                  <Box
                                    sx={{
                                      p: 2,
                                      pt: 0,
                                      textAlign:
                                        customer.status === 'CHECKED_OUT'
                                          ? 'left'
                                          : 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 900,
                                        color: '#1e2b58',
                                        textTransform: 'uppercase',
                                        fontSize: '1.1rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {customer.customerName}
                                    </Typography>

                                    <Box>
                                      {customer.status === 'CHECKED_OUT' ? (
                                        <>
                                          <div className={styles.rowSpecTime}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'green',
                                                fontWeight: 700,
                                                display: 'block',
                                              }}
                                            >
                                              In: {customer.timestamp}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#d32f2f',
                                                fontWeight: 700,
                                                display: 'block',
                                              }}
                                            >
                                              Out: {customer.checkOutDate}
                                            </Typography>
                                          </div>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: '#1976d2',
                                              fontWeight: 700,
                                              display: 'block',
                                            }}
                                          >
                                            Stayed: {customer.duration}
                                          </Typography>
                                        </>
                                      ) : (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#777',
                                            paddingBottom: '8px',
                                            fontWeight: 700,
                                            display: 'block',
                                          }}
                                        >
                                          {customer.timestamp}
                                        </Typography>
                                      )}
                                    </Box>

                                    {customer.status !== 'CHECKED_OUT' && (
                                      <ButtonGroup
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                          boxShadow: 'none',
                                          borderRadius: '12px',
                                          overflow: 'hidden',
                                        }}
                                        disabled={
                                          updatingId === customer.gateEntryId
                                        }
                                      >
                                        <Button
                                          startIcon={
                                            <CheckIcon
                                              sx={{
                                                color: 'white',
                                                fontSize: 26,
                                              }}
                                            />
                                          }
                                          onClick={() =>
                                            this.updateStatus(
                                              customer.gateEntryId,
                                              'ALLOWED'
                                            )
                                          }
                                          sx={{
                                            py: 1.5,
                                            fontWeight: 800,
                                            bgcolor: '#1e2b58',
                                            '&:hover': { bgcolor: '#151d3d' },
                                            border: 'none !important',
                                          }}
                                        >
                                          {updatingId ===
                                            customer.gateEntryId &&
                                          customer.status === 'PENDING' ? (
                                            <CircularProgress
                                              size={20}
                                              color="inherit"
                                            />
                                          ) : (
                                            'Check In'
                                          )}
                                        </Button>
                                      </ButtonGroup>
                                    )}
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </SwiperSlide>
                      ))
                    ) : (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 10,
                          color: 'rgba(255,255,255,0.3)',
                        }}
                      >
                        <Typography variant="h4">
                          No recent entry at the gate!
                        </Typography>
                      </Box>
                    )}
                  </Swiper>
                )}
              </div>
            )}
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════
           DIALOGS  (unchanged — outside the flex layout)
      ══════════════════════════════════════════════════════ */}

        {/* ── Blacklist Alert Dialog ── */}
        {this.props.blacklistAlert?.show && (
          <>
            <Dialog
              fullScreen={this.state.isMobile}
              open={true}
              maxWidth="md"
              fullWidth
              PaperProps={{
                style: {
                  borderRadius: this.state.isMobile ? 0 : 12,
                  overflow: 'hidden',
                },
              }}
            >
              <div
                style={{
                  backgroundColor: '#b71c1c',
                  color: 'white',
                  padding: this.state.isMobile ? '16px' : '24px',
                  textAlign: 'center',
                  borderBottom: '6px solid #801313',
                  textTransform: 'uppercase',
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontSize: this.state.isMobile ? '1.5rem' : '2.2rem',
                    fontWeight: 900,
                  }}
                >
                  <span
                    role="img"
                    aria-label="siren"
                    style={{ marginRight: '10px' }}
                  >
                    🚨
                  </span>
                  Blacklist Alert
                </h1>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: this.state.isMobile ? '0.9rem' : '1.2rem',
                    fontWeight: 500,
                    opacity: 0.9,
                  }}
                >
                  Access Denied - Do Not Allow Entry
                </p>
              </div>

              <DialogContent
                style={{ padding: this.state.isMobile ? 0 : '32px' }}
              >
                {this.state.isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div onClick={() => this.setState({ isImageZoomed: true })}>
                      <img
                        src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                          /\\/g,
                          '/'
                        )}
                        style={{
                          width: '100%',
                          height: '45vh',
                          objectFit: 'cover',
                        }}
                        alt="Customer"
                      />
                    </div>
                    <div
                      style={{
                        marginLeft: '16px',
                        marginRight: '16px',
                        marginTop: '16px',
                        textAlign: 'left',
                      }}
                    >
                      <Typography
                        variant="overline"
                        style={{ color: '#d32f2f', fontWeight: 'bold' }}
                      >
                        CUSTOMER IDENTITY
                      </Typography>
                      <Typography
                        variant="h4"
                        style={{
                          fontWeight: 900,
                          color: '#222',
                          textTransform: 'uppercase',
                        }}
                      >
                        {this.props.blacklistAlert.customer.firstName}{' '}
                        {this.props.blacklistAlert.customer.lastName}
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{ color: '#666', marginBottom: '20px' }}
                      >
                        {this.props.blacklistAlert.customer.phone}
                      </Typography>
                    </div>
                    <div
                      style={{
                        marginLeft: '16px',
                        marginRight: '16px',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: '#fff5f5',
                          border: '2px dashed #d32f2f',
                          padding: '16px',
                          borderRadius: '8px',
                          marginBottom: '24px',
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            color: '#d32f2f',
                            fontWeight: 'bold',
                            display: 'block',
                          }}
                        >
                          REASON
                        </Typography>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 'bold' }}
                        >
                          "{this.props.blacklistAlert.customer.blockReason}"
                        </Typography>
                      </div>
                      <Button
                        fullWidth
                        variant="contained"
                        style={{
                          backgroundColor: '#000',
                          color: '#fff',
                          padding: '16px',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          marginBottom: '24px',
                        }}
                        onClick={() =>
                          this.props.setBlacklistAlert({
                            show: false,
                            customer: null,
                          })
                        }
                      >
                        ACKNOWLEDGE & DISMISS ALERT
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={6}>
                      <div
                        style={{ cursor: 'zoom-in', position: 'relative' }}
                        onClick={() => this.setState({ isImageZoomed: true })}
                      >
                        <img
                          src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                            /\\/g,
                            '/'
                          )}
                          style={{
                            width: '100%',
                            height: '420px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                          }}
                          alt="Customer"
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 12,
                            right: 12,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                          }}
                        >
                          CLICK TO ENLARGE
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="overline"
                        style={{ color: '#d32f2f', fontWeight: 'bold' }}
                      >
                        CUSTOMER IDENTITY
                      </Typography>
                      <Typography
                        variant="h3"
                        style={{
                          fontWeight: 900,
                          color: '#222',
                          marginBottom: '8px',
                          lineHeight: 1.1,
                          textTransform: 'uppercase',
                        }}
                      >
                        {this.props.blacklistAlert.customer.firstName}{' '}
                        {this.props.blacklistAlert.customer.lastName}
                      </Typography>
                      <Typography
                        variant="h6"
                        style={{ color: '#666', marginBottom: '24px' }}
                      >
                        {this.props.blacklistAlert.customer.phone}
                      </Typography>
                      <div
                        style={{
                          backgroundColor: '#fff5f5',
                          borderLeft: '6px solid #d32f2f',
                          padding: '20px',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ color: '#d32f2f', fontWeight: 'bold' }}
                        >
                          RESTRICTION REASON
                        </Typography>
                        <Typography
                          variant="body1"
                          style={{ fontStyle: 'italic', fontSize: '1.1rem' }}
                        >
                          "{this.props.blacklistAlert.customer.blockReason}"
                        </Typography>
                      </div>
                      <Button
                        fullWidth
                        variant="contained"
                        style={{
                          marginTop: '32px',
                          backgroundColor: '#000',
                          color: '#fff',
                          padding: '18px',
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                        }}
                        onClick={() =>
                          this.props.setBlacklistAlert({
                            show: false,
                            customer: null,
                          })
                        }
                      >
                        ACKNOWLEDGE & DISMISS ALERT
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
            </Dialog>

            {/* Zoom dialog */}
            <Dialog
              open={this.state.isImageZoomed}
              onClose={() => this.setState({ isImageZoomed: false })}
              maxWidth="lg"
              PaperProps={{
                style: { backgroundColor: 'transparent', boxShadow: 'none' },
              }}
            >
              <div
                style={{ textAlign: 'center' }}
                onClick={() => this.setState({ isImageZoomed: false })}
              >
                <img
                  src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                    /\\/g,
                    '/'
                  )}
                  style={{
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    border: '2px solid white',
                    borderRadius: '4px',
                  }}
                  alt="Zoomed"
                />
                <p style={{ color: 'white', marginTop: '10px' }}>
                  Click anywhere to close
                </p>
              </div>
            </Dialog>
          </>
        )}

        {/* ── Logout confirm dialog ── */}
        <Dialog
          open={this.state.showLogoutConfirm}
          onClose={() => this.setState({ showLogoutConfirm: false })}
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              bgcolor: '#1a1a1a',
              color: 'white',
              borderRadius: '16px',
              p: 2,
              border: '1px solid rgba(255,255,255,0.1)',
            },
          }}
        >
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              Exit Dashboard?
            </Typography>
            <Typography variant="body1" sx={{ color: '#aaa', mb: 3 }}>
              Are you sure you want to log out of the Gate Security system?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => this.setState({ showLogoutConfirm: false })}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  fontWeight: 700,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => AuthUtil.handleLogout()}
                sx={{ fontWeight: 900, px: 4 }}
              >
                Logout
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* ── Image preview dialog ── */}
        <Dialog
          open={!!previewImage}
          onClose={() => this.setState({ previewImage: null })}
          maxWidth="md"
          TransitionComponent={Zoom}
        >
          <Box
            sx={{
              position: 'relative',
              bgcolor: '#000',
              p: 1,
              display: 'flex',
            }}
          >
            <IconButton
              onClick={() => this.setState({ previewImage: null })}
              sx={{
                position: 'absolute',
                right: 10,
                top: 10,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={`${IMAGE_BASE}${previewImage}`}
              alt="Enlarged"
              style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          </Box>
        </Dialog>
      </div>
    );
  }


}

const mapStateToProps = (state) => ({
  user: userInfoSelector(state),
  blacklistAlert: blacklistAlertSelector(state),
});

const actionCreators = {
  setBlacklistAlert,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  SecurityDashboard
);
